import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileSearch } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { reorderWypisParcels } from "../../api";
import { usePortfolioOptions, useWypisReorderOptions } from "../../hooks";
import { apiErr } from "../../utils";

/**
 * „Zamów ponownie wypisy” — panel zamawiania wypisów EGiB. Wypisy zamawia się zbiorczo,
 * więc i ponowne zgłoszenie jest zbiorcze: wybierasz PROJEKT, zaznaczasz jego działki,
 * które mają już wypis (np. zmienili się właściciele), podajesz jeden powód. Działki
 * wracają do kandydatów w swoich powiatach i dołączysz je do najbliższego wniosku
 * razem z resztą. Starych wypisów nie odpinamy.
 *
 * Po wczytaniu nowego wypisu: właściciel zgodny po nazwisku zostaje, nieobecny dostaje
 * flagę „nieaktualny” (nic nie jest kasowane), nowi dostają rekordy; działki są przeliczane.
 */
export function ReorderWypisModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: portfolio } = usePortfolioOptions();
  const [connection, setConnection] = useState("");
  const [picks, setPicks] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState("");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState<{ flagged: number; skipped: { teryt: string; why: string }[] } | null>(null);
  const { data: options, isLoading } = useWypisReorderOptions(connection || undefined);

  const projectOptions = useMemo(
    () =>
      (portfolio ?? []).map((row) => ({
        value: row.id,
        label: row.project_name + (row.contract_number ? ` (${row.contract_number})` : ""),
      })),
    [portfolio],
  );
  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (options ?? []).filter(
      (r) => !q || r.teryt.toLowerCase().includes(q) || r.obreb.toLowerCase().includes(q)
        || r.county.toLowerCase().includes(q) || r.owners.some((o) => o.toLowerCase().includes(q)),
    );
  }, [options, filter]);
  const selectable = rows.filter((r) => !r.in_active_order && !r.wypis_needed);
  const selected = selectable.filter((r) => picks[r.id]);
  const trimmed = reason.trim();

  const submit = useMutation({
    mutationFn: () => reorderWypisParcels(selected.map((r) => r.id), trimmed),
    onSuccess: (data) => {
      setDone(data);
      setPicks({});
      qc.invalidateQueries({ queryKey: ["pp", "wypis-candidates"] });
      qc.invalidateQueries({ queryKey: ["pp", "wypis-reorder-options"] });
      qc.invalidateQueries({ queryKey: ["pp", "parcels"] });
    },
    onError: (e) => setError(apiErr(e, "Nie udało się zgłosić działek.")),
  });

  return (
    <Modal open onClose={onClose} title="Zamów ponownie wypisy (działki z wypisem)" size="lg">
      <div className="space-y-3 text-xs">
        <p className="text-[var(--color-text-secondary)]">
          Wybierz projekt i zaznacz działki, dla których trzeba wziąć nowy wypis (np. zmienili się
          właściciele). Wrócą do kandydatów w swoich powiatach i dołączysz je do najbliższego
          wniosku razem z innymi działkami. Starego wypisu nie odpinamy. Po wczytaniu nowego:
          właściciel zgodny po nazwisku <b>zostaje</b>, nieobecny w nowym wypisie dostaje flagę{" "}
          <b>nieaktualny</b> (nic nie jest kasowane; jego WPW/WNU chowają się pod „pokaż
          nieaktualnych”), nowi dostają nowe rekordy, a działki są przeliczane.
        </p>

        <label className="block">
          <span className="section-label">Projekt ZE *</span>
          <SearchableSelect
            options={projectOptions}
            value={connection}
            placeholder="Wybierz projekt…"
            onChange={(e) => { setConnection(e.target.value); setPicks({}); setDone(null); setError(""); }}
          />
        </label>

        {connection && (
          isLoading ? (
            <div className="py-4 flex justify-center"><Spinner /></div>
          ) : !options?.length ? (
            <p className="text-[var(--color-text-muted)]">Ten projekt nie ma działek z podpiętym wypisem ani z zamówieniem wypisu w historii.</p>
          ) : (
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className="input-base !py-0.5 text-xs flex-1 min-w-[12rem]"
                  placeholder="Szukaj: nr działki, obręb, powiat, właściciel…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <label className="flex items-center gap-1 cursor-pointer text-[11px] text-[var(--color-text-secondary)]">
                  <input
                    type="checkbox"
                    checked={selectable.length > 0 && selectable.every((r) => picks[r.id])}
                    onChange={() => {
                      const all = selectable.every((r) => picks[r.id]);
                      setPicks((s) => {
                        const next = { ...s };
                        selectable.forEach((r) => { next[r.id] = !all; });
                        return next;
                      });
                    }}
                  />
                  zaznacz wszystkie ({selectable.length})
                </label>
              </div>
              <div className="max-h-[40vh] overflow-y-auto border border-[var(--color-border)]">
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-[var(--color-bg-surface)]">
                    <tr className="text-left">
                      <th className="px-2 py-1 w-6"> </th>
                      <th className="px-2 py-1">Nr działki ewidencyjnej</th>
                      <th className="px-2 py-1">Obręb · gmina</th>
                      <th className="px-2 py-1">Powiat</th>
                      <th className="px-2 py-1">Właściciele (aktualni)</th>
                      <th className="px-2 py-1">Wypisy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const blocked = r.in_active_order || r.wypis_needed;
                      return (
                        <tr
                          key={r.id}
                          className={`border-t border-[var(--color-border)] ${blocked ? "opacity-60" : "hover:bg-[var(--color-bg-elevated)] cursor-pointer"}`}
                          onClick={() => { if (!blocked) setPicks((s) => ({ ...s, [r.id]: !s[r.id] })); }}
                        >
                          <td className="px-2 py-1">
                            <input type="checkbox" checked={!!picks[r.id]} disabled={blocked} readOnly />
                          </td>
                          <td className="px-2 py-1 font-mono">
                            {r.teryt}
                            {r.in_active_order && (
                              <span className="ml-1"><Badge color="blue">w zamówieniu</Badge></span>
                            )}
                            {!r.in_active_order && r.wypis_needed && (
                              <span className="ml-1" title={r.wypis_needed_reason || undefined}><Badge color="yellow">zgłoszona</Badge></span>
                            )}
                          </td>
                          <td className="px-2 py-1">{[r.obreb, r.commune].filter(Boolean).join(" · ") || "—"}</td>
                          <td className="px-2 py-1">{r.county || "—"}</td>
                          <td className="px-2 py-1">{r.owners.join(", ") || "—"}</td>
                          <td className="px-2 py-1 font-mono">
                            {r.wypisy_total}
                            {r.wypisy_total === 0 && r.wypis_orders_total > 0 && (
                              <span className="ml-1" title="Wypis został odpięty od działki, ale zamówienie na nią było."><Badge color="orange">wypis odpięty</Badge></span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        <label className="block">
          <span className="section-label">Powód ponownego zamówienia *</span>
          <textarea
            className="input-base mt-0.5 w-full text-xs"
            rows={2}
            maxLength={255}
            value={reason}
            placeholder="np. zmiana właścicieli po sprzedaży działek"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>

        {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
        {done && (
          <div className="border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 space-y-1">
            <div>Zgłoszono do ponownego zamówienia: <b>{done.flagged}</b>. Działki czekają w panelu zamawiania w swoich powiatach.</div>
            {done.skipped.length > 0 && (
              <div className="text-[var(--color-text-secondary)]">
                Pominięte: {done.skipped.map((s) => `${s.teryt} (${s.why})`).join(", ")}.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submit.isPending}>
            {done ? "Zamknij" : "Anuluj"}
          </Button>
          <Button
            onClick={() => { setError(""); submit.mutate(); }}
            disabled={submit.isPending || selected.length === 0 || !trimmed}
            loading={submit.isPending}
            title={!trimmed ? "Podaj powód ponownego zamówienia." : selected.length === 0 ? "Zaznacz działki." : undefined}
          >
            <FileSearch size={13} className="mr-1" />
            Zgłoś do ponownego zamówienia ({selected.length})
          </Button>
        </div>
      </div>
    </Modal>
  );
}
